import {html, LitElement} from 'lit';
import {customElement, property, queryAssignedElements, state} from 'lit/decorators.js';
import {Logger} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import general from '../../ts/css/general.styles';
import elements from '../../ts/css/elements.styles';
import {resetStyles} from '../../ts/css/resets.styles';
import {iconsFont} from '../../ts/css/icons.styles';
import styles from './horoscope.styles';

@customElement('ui-horoscopeslider')
export class Horoscope extends LitElement {
  static readonly styles = [resetStyles, general, elements, iconsFont, styles];
  currentIndex_ = 0;
  pages_ = 0;
  currentPage_ = 0;
  name_ = 'horoscope';
  signCount_ = 0;
  slider_: HTMLUListElement;
  signs_: HTMLLIElement[];
  scrollTimer_: undefined | number;
  wideModeSignCount_ = 5; // signs to see at once in wide mode
  wideMode_ = false;
  signWidth_ = 0; // width of each sign, for calculate scroll pixels

  @property() mode: 'tool' | 'line';
  @state() disablePrevButton = true;
  @state() disableNextButton = false;
  @queryAssignedElements({selector: '.zodiac-list', flatten: false}) slider: HTMLUListElement[];

  connectedCallback() {
    super.connectedCallback();
  }

  protected render() {
    return html`
      <div class="zodiacSliderOuter${this.mode ? ' tool' : null}">
        <div class="zodiacSlider clearfix">
          <div class="left icon icon-thin-arrow-back ${this.disablePrevButton ? 'disabled' : null}"
               @click=${this.prev}></div>
          <slot @slotchange="${this.handleSlotChange}"></slot>
          <div class="right icon icon-thin-arrow-next ${this.disableNextButton ? 'disabled' : null}"
               @click=${this.next}></div>
        </div>
      </div>
    `;
  }

  handleSlotChange() {
    this.slider_ = this.slider[0];
    this.signs_ = [...this.slider_.querySelectorAll('li')];
    this.signCount_ = this.signs_.length;
    this.signWidth_ = this.signs_[0].offsetWidth;

    // attach event handler
    this.slider_.addEventListener('scroll', this.handleScrollEnd.bind(this));

    // special case: "full-width tool"
    if (this.mode == 'tool') {
      if (this.slider_ && (this.slider_.getBoundingClientRect().width >= 550)) {
        this.wideMode_ = true;
        this.slider_.classList.add('widemode');
      }
    } else {
      this.wideMode_ = Viewport.matches('tablet');
    }

    this.pages_ = this.wideMode_ ? Math.ceil(this.signCount_ / this.wideModeSignCount_) : (this.signCount_);
    this.currentPage_ = this.pages_ > 0 ? 1 : 0;

    this.updatePosition(0); // (reset) initial position
  }

  /**
   * Show current index
   */
  updatePosition(direction: -1 | 0 | 1 = 0): void {
    /*
    // [CADNPCA-16081] Firefox does not support "inline" parameter -> overscroll
    this.signs_[this.currentIndex_].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    }); */
    this.slider_.scrollBy({
      top: 0,
      left: (this.signWidth_ * (this.wideMode_ ? this.wideModeSignCount_ : 1) * direction),
      behavior: 'smooth'
    });
    Logger.log(this.name_, `Current index: ${this.currentIndex_}`);
    this.showHideArrows();
  }

  /**
   * Shows or hides nav-arrows if at first or last element
   */
  showHideArrows() {
    // show/hide arrow
    this.disableNextButton = (this.currentIndex_ >= (this.signCount_ - 1));

    if (this.wideMode_) {
      this.disablePrevButton = (this.currentIndex_ <= 4);
    } else {
      this.disablePrevButton = (this.currentIndex_ <= 0);
    }
  }

  /**
   * Updates current index after user swipe/scrolling
   */
  updatePositionForSwipe() {
    const lpos = this.slider_.scrollLeft; // !
    const w = this.slider_.scrollWidth;
    const offset = this.wideMode_ ? (this.wideModeSignCount_ - 1) : 0;
    const idx = Math.round(lpos * this.signCount_ / w) + offset;
    Logger.log(this.name_, `Scroll position: ${lpos}, index = ${idx}`);
    this.currentIndex_ = idx;
    this.showHideArrows();
  }

  /**
   * Show previous item -> scroll right
   */
  prev() {
    if (this.currentPage_ > 1) {
      this.currentPage_--;
      this.currentIndex_ = (this.currentPage_ - 1);

      if (this.wideMode_) {
        this.currentIndex_ = ((this.currentPage_ * this.wideModeSignCount_) - this.wideModeSignCount_); // 5 visible in wide mode
      }
      this.updatePosition(-1);
    }
  }

  /**
   * Show next item -> scroll left
   */
  next() {
    if (this.currentPage_ < this.pages_) {
      this.currentPage_++;
      this.currentIndex_ = (this.currentPage_ - 1); // zero based index, currentPage is one based index

      if (this.wideMode_) {
        this.currentIndex_ = Math.min((this.currentPage_ * this.wideModeSignCount_ - 1), this.signCount_ - 1);
      }
      this.updatePosition(1);
    }
  }

  handleScrollEnd() {
    if (this.scrollTimer_) {
      window.clearTimeout(this.scrollTimer_);
    }
    this.scrollTimer_ = window.setTimeout(() => {
      this.updatePositionForSwipe();
    }, 100);
  }
}
